import React from "react";
import dayjs, { Dayjs } from "dayjs";
import {
    Typography,
    Button,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    Checkbox,
    Popover
} from "@mui/material";

import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";

import ErrorDialogue from "../../Components/ErrorDialogue/ErrorDialogue";
import Spinner from "../../Components/Spinner/Spinner";
import InlineProgressBar from "../../Components/InlineProgressBar/InlineProgressBar";
import { AuthContext, AuthContextType } from "../../Types/AuthContext";
import CountPeriodRepository from "../../Repositories/CountPeriodRepository";
import { CountPeriodType } from "../../Types/Enums/CountPeriodType";
import CountPeriod from "../../Types/CountPeriod";
import WorkerData from "../../Types/WorkerData";
import WorkerRepository from "../../Repositories/WorkerRepository";
import TransactionRepository from "../../Repositories/TransactionRepository";
import UpdateCountPeriod from "../../Repositories/CountPeriodDataModels/UpdateCountPeriod";
import { Functions } from "../../Types/Enums/Functions";
import CountPeriodResponse from "../../Repositories/CountPeriodResponses/CountPeriodResponse";
import CountPeriodWorkerCountEventResponse, { CountPeriodWorkerCountAction } from "../../Repositories/CountPeriodResponses/CountPeriodWorkerCountEventResponse";
import TipCalculationService from "../../Services/TipCalculationService";
import CurrencyRoundingSetting from "../../Components/SettingsComponents/CurrencyRoundingSetting";

type ManualCountWorkerCountEvent = CountPeriodWorkerCountEventResponse;

type ManualCountWorkerRow = {
    workerId: string;
    name: string;
    function: Functions;
    debt: number;
    pinTip: number;
    cashTip: number;
    totalTip: number;
    isCounted: boolean;
    countEvents: ManualCountWorkerCountEvent[];
    hasUncountedEvent: boolean;
};

interface ManualCountPageState {
    isLoading: boolean;
    selectedMonth: Dayjs;
    countPeriod: CountPeriod | null;
    notCountedWorkerIds: string[];
    workers: WorkerData[];
    rows: ManualCountWorkerRow[];
    receivedPin: string;
    receivedCash: string;
    receivedTouched: boolean;
    uncountedPopoverAnchor: HTMLElement | null;
    selectedCountEvents: ManualCountWorkerCountEvent[];
    selectedCountEventsWorkerName: string;
    payoutOptionsAnchor: HTMLElement | null;
    tipEditAnchor: HTMLElement | null;
    tipEditWorkerId: string | null;
    tipEditValue: string;
    // Per-worker manual replacement for the calculated payout, keyed by
    // workerId. Consumed when the worker is counted; cleared on month change.
    manualTipOverrides: Record<string, number>;
    errorTitle?: string;
    errorMsg?: string;
}

export default class ManualCountPage extends React.Component<{}, ManualCountPageState> {
    static contextType = AuthContext;
    context!: AuthContextType;

    private accent = "#c79656";
    private border = "rgba(0,0,0,0.08)";

    // Used to turn a worker's outstanding tips + carried debt into a payable
    // amount; the leftover is the new debt. Configured from the location's
    // payout settings via the CurrencyRoundingSetting control.
    private debtService = new TipCalculationService();

    // Payouts are derived during render from debtService, which lives outside
    // component state — so a settings change has to force a redraw itself.
    private onDebtServiceChanged = () => this.setState(s => ({ ...s }));

    public constructor(props: {}) {
        super(props);

        this.loadMonth = this.loadMonth.bind(this);
        this.goToPreviousMonth = this.goToPreviousMonth.bind(this);
        this.goToNextMonth = this.goToNextMonth.bind(this);
        this.markWorkerAsCounted = this.markWorkerAsCounted.bind(this);
        this.unmarkWorkerAsCounted = this.unmarkWorkerAsCounted.bind(this);
        this.toggleWorkerCounted = this.toggleWorkerCounted.bind(this);
        this.openUncountedPopover = this.openUncountedPopover.bind(this);
        this.closeUncountedPopover = this.closeUncountedPopover.bind(this);
        this.closeTipEditPopover = this.closeTipEditPopover.bind(this);
        this.applyTipEdit = this.applyTipEdit.bind(this);
        this.resetTipEdit = this.resetTipEdit.bind(this);
        this.onReceivedPinChange = this.onReceivedPinChange.bind(this);
        this.onReceivedCashChange = this.onReceivedCashChange.bind(this);
        this.onReceivedSubmit = this.onReceivedSubmit.bind(this);
        this.formatCurrency = this.formatCurrency.bind(this);

        this.state = {
            isLoading: true,
            selectedMonth: dayjs().startOf("month"),
            countPeriod: null,
            notCountedWorkerIds: [],
            workers: [],
            rows: [],
            receivedPin: "",
            receivedCash: "",
            receivedTouched: false,
            uncountedPopoverAnchor: null,
            selectedCountEvents: [],
            selectedCountEventsWorkerName: "",
            payoutOptionsAnchor: null,
            tipEditAnchor: null,
            tipEditWorkerId: null,
            tipEditValue: "",
            manualTipOverrides: {},
            errorTitle: "",
            errorMsg: ""
        };

        this.onCurrencySettingsChange = this.onCurrencySettingsChange.bind(this);
    }

    private onCurrencySettingsChange(currencies: number[], exactMode: boolean): void {
        this.debtService.SetValidCurrencyValues(currencies);
        this.debtService.SetExactCalculation(exactMode);
    }

    private roundMoney(value: number): number {
        return Math.round((value + 1e-9) * 100) / 100;
    }

    // The payout the calculation service proposes for this row, before any
    // manual adjustment by the user.
    private getCalculatedPaidTip(row: ManualCountWorkerRow): number {
        const payout = this.debtService.CalculateTip(row.cashTip, row.pinTip, row.debt);
        return this.roundMoney(payout.roundedTip);
    }

    // The tip that will actually be paid when this row is counted: the user's
    // manual override if one is set, otherwise the calculated payout.
    private getPlannedPaidTip(row: ManualCountWorkerRow): number {
        const override = this.state.manualTipOverrides[row.workerId];
        return override !== undefined ? override : this.getCalculatedPaidTip(row);
    }

    private computeCountDebt(row: ManualCountWorkerRow): { oldDebt: number; newDebt: number; delta: number } {
        const oldDebt = row.debt;
        const paidTip = this.getPlannedPaidTip(row);
        const newDebt = this.roundMoney(oldDebt + row.totalTip - paidTip);
        return { oldDebt, newDebt, delta: this.roundMoney(newDebt - oldDebt) };
    }

    // The debt a not-yet-counted worker would move to when counted, so the
    // user can preview the change up front.
    private getRowDebtPreview(row: ManualCountWorkerRow): { from: number; to: number } {
        const { oldDebt, newDebt } = this.computeCountDebt(row);
        return { from: oldDebt, to: newDebt };
    }

    // A count event's debt delta is the earned tip minus the amount that was
    // actually paid. Use the recorded delta so a completed payout keeps showing
    // the original result even if the rounding settings are changed later.
    private getDisplayedTotalTip(row: ManualCountWorkerRow): number {
        if (!row.isCounted) {
            return row.totalTip;
        }

        return this.roundMoney(row.totalTip - this.getLastAppliedDebtDelta(row));
    }

    // The tip a not-yet-counted worker would actually be paid when counted
    // (earned tips plus carried debt, fitted to the payout denominations),
    // mirroring getRowDebtPreview.
    private getRowTipPreview(row: ManualCountWorkerRow): { from: number; to: number } {
        const { delta } = this.computeCountDebt(row);
        return { from: row.totalTip, to: this.roundMoney(row.totalTip - delta) };
    }

    private renderTotalTipCell(row: ManualCountWorkerRow): React.ReactNode {
        const cellStyle: React.CSSProperties = {
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            fontVariantNumeric: "tabular-nums",
            minWidth: 0,
            overflow: "hidden",
            whiteSpace: "nowrap"
        };

        // Counted: the payout happened, so show the tip that was actually
        // given with the earned tip in brackets behind it.
        if (row.isCounted) {
            const paidTip = this.getDisplayedTotalTip(row);
            const changed = this.roundMoney(paidTip - row.totalTip) !== 0;
            const paidColor = !changed ? "#1a1a1a" : paidTip > row.totalTip ? "#12a037ff" : "#c52626ff";

            return (
                <div title={`Paid tip (earned ${this.formatCurrency(row.totalTip)})`} style={cellStyle}>
                    <span style={{ fontWeight: 800, fontSize: 12.5, color: paidColor }}>{this.formatCurrency(paidTip)}</span>
                    {changed ? (
                        <span style={{ color: "rgba(0,0,0,0.4)" }}>({this.formatCurrency(row.totalTip)})</span>
                    ) : null}
                </div>
            );
        }

        const { from, to } = this.getRowTipPreview(row);
        const changed = this.roundMoney(to - from) !== 0;
        const isOverridden = this.state.manualTipOverrides[row.workerId] !== undefined;
        const toColor = !changed ? "#1a1a1a" : to > from ? "#12a037ff" : "#c52626ff";

        const paidTipButton = (
            <button
                type="button"
                onClick={(event) => this.openTipEditPopover(event, row)}
                disabled={this.state.isLoading}
                aria-label={`Adjust paid tip for ${row.name}`}
                title={isOverridden ? "Manually adjusted — click to change" : "Click to adjust the paid tip"}
                style={{
                    background: "none",
                    border: "none",
                    padding: "1px 2px",
                    margin: 0,
                    font: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    cursor: "pointer",
                    fontWeight: changed ? 700 : 800,
                    fontSize: changed ? 12 : 12.5,
                    color: toColor,
                    fontVariantNumeric: "tabular-nums",
                    textDecoration: "underline dotted",
                    textDecorationColor: `${this.accent}bb`,
                    textUnderlineOffset: 3,
                    borderRadius: 4
                }}
            >
                {this.formatCurrency(to)}
                {isOverridden ? <EditRoundedIcon sx={{ fontSize: 12, color: this.accent }} /> : null}
            </button>
        );

        return (
            <div title="Paid tip if you count this worker" style={cellStyle}>
                {changed ? (
                    <>
                        <span style={{ color: "rgba(0,0,0,0.4)" }}>{this.formatCurrency(from)}</span>
                        <span style={{ color: "rgba(0,0,0,0.3)" }}>→</span>
                        {paidTipButton}
                    </>
                ) : (
                    paidTipButton
                )}
            </div>
        );
    }

    private renderDebtPreviewCell(row: ManualCountWorkerRow): React.ReactNode {
        const cellStyle: React.CSSProperties = {
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            fontVariantNumeric: "tabular-nums",
            minWidth: 0,
            overflow: "hidden",
            whiteSpace: "nowrap"
        };

        // Counted: the debt column already reflects reality, so just repeat
        // the current debt here without a preview arrow.
        if (row.isCounted) {
            const color = row.debt > 0 ? "#12a037ff" : row.debt < 0 ? "#c52626ff" : "rgba(0,0,0,0.6)";

            return (
                <div title="Current debt" style={cellStyle}>
                    <span style={{ fontWeight: 700, color }}>{this.formatCurrency(row.debt)}</span>
                </div>
            );
        }

        const { from, to } = this.getRowDebtPreview(row);
        const changed = this.roundMoney(to - from) !== 0;
        const toColor = to > 0 ? "#12a037ff" : to < 0 ? "#c52626ff" : "rgba(0,0,0,0.6)";

        return (
            <div title="Debt if you count this worker" style={cellStyle}>
                {changed ? (
                    <>
                        <span style={{ color: "rgba(0,0,0,0.4)" }}>{this.formatCurrency(from)}</span>
                        <span style={{ color: "rgba(0,0,0,0.3)" }}>→</span>
                        <span style={{ fontWeight: 700, color: toColor }}>{this.formatCurrency(to)}</span>
                    </>
                ) : (
                    <span style={{ color: "rgba(0,0,0,0.45)" }}>{this.formatCurrency(to)}</span>
                )}
            </div>
        );
    }

    public componentDidMount(): void {
        this.debtService.AddPropertyChangedListener(this.onDebtServiceChanged);

        // Allow deep links like /counting/manual?month=2026-03 (used by the
        // CSV import page) to open a specific month directly.
        const monthParam = new URLSearchParams(window.location.search).get("month");
        const parsedMonth = monthParam ? dayjs(monthParam) : null;

        this.loadMonth(parsedMonth && parsedMonth.isValid() ? parsedMonth.startOf("month") : this.state.selectedMonth);
    }

    public componentWillUnmount(): void {
        this.debtService.RemovePropertyChangedListener(this.onDebtServiceChanged);
    }

    private toCountPeriod(item: CountPeriodResponse): CountPeriod {
        const parsedCountUpdatedOn = item.countUpdatedOn ? dayjs(item.countUpdatedOn) : null;

        return {
            id: item.id,
            locationId: item.locationId,
            transactionId: item.transactionId ?? null,
            type: item.type,
            startDate: dayjs(item.startDate),
            endDate: dayjs(item.endDate),
            createdBy: item.createdBy,
            createdOn: item.createdOn,
            countedWorkers: item.countedWorkers,
            countUpdatedBy: item.countUpdatedBy,
            countUpdatedOn: parsedCountUpdatedOn && parsedCountUpdatedOn.isValid() ? parsedCountUpdatedOn : null
        } as CountPeriod;
    }

    private getExistingCountPeriodForMonth(month: Dayjs, countPeriods: CountPeriodResponse[]): CountPeriodResponse | null {
        const monthStart = month.startOf("month");
        const monthEnd = month.endOf("month");

        return countPeriods
            .filter((item) =>
                dayjs(item.startDate).isSame(monthStart, "day") &&
                dayjs(item.endDate).isSame(monthEnd, "day")
            )
            .sort((a, b) => {
                const transactionScore = Number(Boolean(b.transactionId)) - Number(Boolean(a.transactionId));
                if (transactionScore !== 0) {
                    return transactionScore;
                }

                const countedWorkersScore = (b.countedWorkers?.length ?? 0) - (a.countedWorkers?.length ?? 0);
                if (countedWorkersScore !== 0) {
                    return countedWorkersScore;
                }

                return dayjs(b.createdOn).valueOf() - dayjs(a.createdOn).valueOf();
            })[0] ?? null;
    }

    private async loadMonth(month: Dayjs): Promise<void> {
        // Manual tip overrides stay valid while reloading the same month
        // (e.g. after counting a worker), but not when switching months.
        const keptOverrides = month.startOf("month").isSame(this.state.selectedMonth, "month")
            ? this.state.manualTipOverrides
            : {};

        this.setState({
            isLoading: true,
            selectedMonth: month.startOf("month"),
            errorTitle: "",
            errorMsg: ""
        });

        const countPeriod = await this.getOrCreateCountPeriod(month.startOf("month"));
        if (!countPeriod) {
            this.setState({
                isLoading: false,
                countPeriod: null,
                notCountedWorkerIds: [],
                workers: [],
                rows: [],
                receivedPin: "",
                receivedCash: "",
                receivedTouched: false,
                uncountedPopoverAnchor: null,
                selectedCountEvents: [],
                selectedCountEventsWorkerName: "",
                tipEditAnchor: null,
                tipEditWorkerId: null,
                tipEditValue: "",
                manualTipOverrides: {}
            });
            return;
        }

        const [notCountedWorkerIds, workers] = await Promise.all([
            this.getNotCountedWorkers(countPeriod.id),
            this.getAllWorkers()
        ]);

        const countEvents = await this.getWorkerCountEvents(countPeriod.id);
        const workersWithTips = await this.getWorkersWithTips(countPeriod, workers);
        const rows = this.buildRows(workersWithTips, notCountedWorkerIds, countPeriod.countedWorkers, countEvents);
        const receivedCashNumber = rows.reduce((sum, row) => sum + row.cashTip, 0);
        const receivedCash = receivedCashNumber.toFixed(2);
        const receivedPin = await this.getReceivedPinAmount(countPeriod, receivedCashNumber);

        this.setState({
            isLoading: false,
            selectedMonth: month.startOf("month"),
            countPeriod,
            notCountedWorkerIds,
            workers: workersWithTips,
            rows,
            receivedPin,
            receivedCash,
            receivedTouched: false,
            uncountedPopoverAnchor: null,
            selectedCountEvents: [],
            selectedCountEventsWorkerName: "",
            tipEditAnchor: null,
            tipEditWorkerId: null,
            tipEditValue: "",
            manualTipOverrides: keptOverrides
        });
    }

    private async getOrCreateCountPeriod(month: Dayjs): Promise<CountPeriod | null> {
        const result = await CountPeriodRepository.getAllCountPeriods(
            this.context.locationId,
            undefined,
            CountPeriodType.Month,
            month.startOf("month"),
            month.endOf("month")
        );

        if (result.isFailed() || !result.result) {
            this.setState({
                errorTitle: result.errorTitle ?? "Failed to load month",
                errorMsg: result.errorMessage ?? "Something went wrong while loading the month"
            });
            return null;
        }

        const existingCountPeriod = this.getExistingCountPeriodForMonth(month, result.result.countPeriods);
        if (existingCountPeriod) {
            return this.toCountPeriod(existingCountPeriod);
        }

        const createResult = await CountPeriodRepository.addCountPeriod(
            this.context.locationId,
            null,
            CountPeriodType.Month,
            month.startOf("month"),
            month.endOf("month")
        );

        if (createResult.isFailed() || !createResult.result) {
            this.setState({
                errorTitle: createResult.errorTitle ?? "Failed to create month",
                errorMsg: createResult.errorMessage ?? "Something went wrong while creating the month"
            });
            return null;
        }

        return {
            id: createResult.result.id,
            locationId: this.context.locationId,
            transactionId: null,
            type: CountPeriodType.Month,
            startDate: month.startOf("month"),
            endDate: month.endOf("month"),
            createdBy: this.context.userId,
            createdOn: dayjs().format(),
            countedWorkers: [],
            countUpdatedBy: [this.context.userId],
            countUpdatedOn: dayjs()
        };
    }

    private async getNotCountedWorkers(countPeriodId: string): Promise<string[]> {
        const result = await CountPeriodRepository.getNotCountedWorkers(this.context.locationId, countPeriodId);

        if (result.isFailed() || !result.result) {
            return [];
        }

        return result.result.notCountedWorkers.map((x) => x.id);
    }

    private async getWorkerCountEvents(countPeriodId: string): Promise<ManualCountWorkerCountEvent[]> {
        const result = await CountPeriodRepository.getCountPeriodWorkerCountEvents(this.context.locationId, countPeriodId);

        if (result.isFailed() || !result.result) {
            return [];
        }

        return result.result.events;
    }

    private async getReceivedPinAmount(countPeriod: CountPeriod, receivedCash: number): Promise<string> {
        if (!countPeriod.transactionId) {
            return "";
        }

        const result = await TransactionRepository.getTransactionById(this.context.locationId, countPeriod.transactionId);
        if (result.isFailed() || !result.result?.transaction) {
            return "";
        }

        const totalReceived = result.result.transaction.amount;
        const pinAmount = totalReceived - receivedCash;

        return Math.max(0, pinAmount).toFixed(2);
    }

    private async getAllWorkers(): Promise<WorkerData[]> {
        const result = await WorkerRepository.getAllWorkers(this.context.locationId);

        if (result.isFailed() || !result.result) {
            this.setState({
                errorTitle: result.errorTitle ?? "Failed to load workers",
                errorMsg: result.errorMessage ?? "Something went wrong while loading workers"
            });
            return [];
        }

        return result.result.response.map(
            (x) =>
                new WorkerData(
                    x.id,
                    x.userId,
                    x.firstName,
                    x.lastName,
                    x.debt,
                    0,
                    x.permissions,
                    x.function,
                    0,
                    0
                )
        );
    }

    private async getWorkersWithTips(countPeriod: CountPeriod, workers: WorkerData[]): Promise<WorkerData[]> {
        if (!workers.length) {
            return [];
        }

        const result = await WorkerRepository.getWorkersTips(
            this.context.locationId,
            countPeriod.startDate,
            countPeriod.endDate
        );

        if (result.isFailed() || !result.result) {
            return workers;
        }

        return workers.map((worker) => {
            const tipData = result.result!.workers.find((x) => x.id === worker.Guid);

            if (!tipData) {
                return worker;
            }

            worker.CashTip = tipData.cashTip;
            worker.PinTip = tipData.pinTip;

            return worker;
        });
    }

    private isUncountedEvent(event: ManualCountWorkerCountEvent): boolean {
        return event.action === CountPeriodWorkerCountAction.Uncounted || event.action === "Uncounted";
    }

    private isCountedEvent(event: ManualCountWorkerCountEvent): boolean {
        return event.action === CountPeriodWorkerCountAction.Counted || event.action === "Counted";
    }

    private getCountEventsByWorkerId(countEvents: ManualCountWorkerCountEvent[]): Map<string, ManualCountWorkerCountEvent[]> {
        const eventsByWorkerId = new Map<string, ManualCountWorkerCountEvent[]>();

        countEvents
            .sort((a, b) => dayjs(a.updatedOn).valueOf() - dayjs(b.updatedOn).valueOf())
            .forEach((event) => {
                const workerEvents = eventsByWorkerId.get(event.workerId) ?? [];
                workerEvents.push(event);
                eventsByWorkerId.set(event.workerId, workerEvents);
            });

        return eventsByWorkerId;
    }

    private buildRows(workers: WorkerData[], notCountedWorkerIds: string[], countedWorkerIds: string[], countEvents: ManualCountWorkerCountEvent[]): ManualCountWorkerRow[] {
        const allowedWorkerIds = new Set([...notCountedWorkerIds, ...countedWorkerIds]);
        const notCountedWorkerIdSet = new Set(notCountedWorkerIds);
        const countEventsByWorkerId = this.getCountEventsByWorkerId(countEvents);

        return workers
            .filter((worker) => allowedWorkerIds.has(worker.Guid))
            .map((worker) => {
                const cashTip = worker.CashTip;
                const pinTip = worker.PinTip;
                const debt = worker.Debt;
                const isCounted = !notCountedWorkerIdSet.has(worker.Guid);
                const workerCountEvents = countEventsByWorkerId.get(worker.Guid) ?? [];

                return {
                    workerId: worker.Guid,
                    name: `${worker.FirstName} ${worker.LastName}`.trim(),
                    function: worker.Function,
                    debt,
                    pinTip,
                    cashTip,
                    totalTip: cashTip + pinTip,
                    isCounted,
                    countEvents: workerCountEvents,
                    hasUncountedEvent: workerCountEvents.some((event) => this.isUncountedEvent(event))
                };
            })
            .sort((a, b) => {
                const functionCompare = a.function - b.function;
                if (functionCompare !== 0) {
                    return functionCompare;
                }

                return a.name.localeCompare(b.name);
            });
    }

    private async goToPreviousMonth(): Promise<void> {
        await this.loadMonth(this.state.selectedMonth.subtract(1, "month").startOf("month"));
    }

    private async goToNextMonth(): Promise<void> {
        await this.loadMonth(this.state.selectedMonth.add(1, "month").startOf("month"));
    }

    private onReceivedPinChange(e: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            receivedPin: e.target.value,
            receivedTouched: true
        });
    }

    private onReceivedCashChange(e: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            receivedCash: e.target.value,
            receivedTouched: true
        });
    }

    private async onReceivedSubmit(): Promise<void> {
        const { countPeriod, receivedPin, receivedCash } = this.state;

        if (!countPeriod) {
            return;
        }

        const rawPin = receivedPin.trim();
        const normalizedPin = rawPin.replace(",", ".");
        const pinAmount = Number.parseFloat(normalizedPin);

        const rawCash = receivedCash.trim();
        const normalizedCash = rawCash.replace(",", ".");
        const cashAmount = Number.parseFloat(normalizedCash);

        if (!rawPin || !rawCash || Number.isNaN(pinAmount) || Number.isNaN(cashAmount)) {
            this.setState({ receivedTouched: true });
            return;
        }

        if (countPeriod.transactionId) {
            return;
        }

        this.setState({ isLoading: true });

        const result = await TransactionRepository.addTransaction(
            this.context.locationId,
            pinAmount + cashAmount,
            `Received tip for ${countPeriod.startDate.format("MMMM YYYY")}`
        );

        if (result.isFailed() || !result.result) {
            this.setState({
                isLoading: false,
                errorTitle: result.errorTitle ?? "Unexpected error",
                errorMsg: result.errorMessage ?? "An unexpected error occured"
            });
            return;
        }

        const updateModel: UpdateCountPeriod = {
            transactionId: result.result.id
        };

        const updateResult = await CountPeriodRepository.updateCountPeriod(
            this.context.locationId,
            countPeriod.id,
            updateModel
        );

        if (updateResult.isFailed() || !updateResult.result) {
            this.setState({
                isLoading: false,
                errorTitle: updateResult.errorTitle ?? "Unexpected error",
                errorMsg: updateResult.errorMessage ?? "An unexpected error occured"
            });
            return;
        }

        await this.loadMonth(this.state.selectedMonth);
    }

    private async markWorkerAsCounted(workerId: string): Promise<void> {
        const previousState = this.state;
        const { countPeriod, notCountedWorkerIds, rows } = previousState;

        if (!countPeriod || !notCountedWorkerIds.includes(workerId)) {
            return;
        }

        const row = rows.find((r) => r.workerId === workerId);
        if (!row) {
            return;
        }

        // Counting a worker settles their outstanding tips: pay out the fitted
        // amount and carry the leftover as the worker's new debt.
        const { newDebt, delta } = this.computeCountDebt(row);

        const now = dayjs();
        const optimisticCountedEvent: ManualCountWorkerCountEvent = {
            id: "",
            countPeriodId: countPeriod.id,
            workerId,
            action: CountPeriodWorkerCountAction.Counted,
            updatedBy: this.context.userId,
            updatedByName: this.context.fullName || this.context.userId,
            updatedOn: now.toISOString(),
            debtDelta: delta
        };

        const updatedCountPeriod: CountPeriod = {
            ...countPeriod,
            countedWorkers: [...countPeriod.countedWorkers, workerId],
            countUpdatedBy: [this.context.userId],
            countUpdatedOn: now
        };

        const updatedNotCountedWorkerIds = previousState.notCountedWorkerIds.filter((id) => id !== workerId);
        const updatedRows = previousState.rows.map((r) =>
            r.workerId === workerId
                ? {
                    ...r,
                    debt: newDebt,
                    isCounted: true,
                    countEvents: [...r.countEvents, optimisticCountedEvent],
                    hasUncountedEvent: r.hasUncountedEvent
                }
                : r
        );

        // A manual tip override is consumed by counting: the recorded delta
        // now carries the adjustment.
        const updatedOverrides = { ...previousState.manualTipOverrides };
        delete updatedOverrides[workerId];

        this.setState({
            isLoading: true,
            countPeriod: updatedCountPeriod,
            notCountedWorkerIds: updatedNotCountedWorkerIds,
            rows: updatedRows,
            manualTipOverrides: updatedOverrides,
            errorTitle: "",
            errorMsg: ""
        });

        // Mark counted first (cheaply reversible), then persist the debt; the
        // delta is stored on the count event so an undo can reverse it.
        const addResult = await CountPeriodRepository.addCountedWorker(
            this.context.locationId,
            countPeriod.id,
            workerId,
            delta
        );

        if (addResult.isFailed() || !addResult.result) {
            this.restoreCountState(previousState, addResult.errorTitle, addResult.errorMessage, "Could not mark worker as counted");
            return;
        }

        const debtResult = await WorkerRepository.setWorkerDebt(this.context.locationId, workerId, newDebt);
        if (debtResult.isFailed()) {
            await CountPeriodRepository.removeCountedWorker(this.context.locationId, countPeriod.id, workerId, -delta);
            this.restoreCountState(previousState, debtResult.errorTitle, debtResult.errorMessage, "Could not store the worker's debt");
            return;
        }

        await this.loadMonth(this.state.selectedMonth);
    }

    private restoreCountState(previousState: ManualCountPageState, errorTitle: string | undefined, errorMessage: string | undefined, fallbackMessage: string): void {
        this.setState({
            isLoading: previousState.isLoading,
            countPeriod: previousState.countPeriod,
            notCountedWorkerIds: previousState.notCountedWorkerIds,
            rows: previousState.rows,
            uncountedPopoverAnchor: previousState.uncountedPopoverAnchor,
            selectedCountEvents: previousState.selectedCountEvents,
            selectedCountEventsWorkerName: previousState.selectedCountEventsWorkerName,
            manualTipOverrides: previousState.manualTipOverrides,
            errorTitle: errorTitle ?? "Failed to update month",
            errorMsg: errorMessage ?? fallbackMessage
        });
    }

    private getLastAppliedDebtDelta(row: ManualCountWorkerRow): number {
        // The debt added by the most recent "counted" action is what an undo
        // must reverse, so the worker's debt returns to its pre-count value.
        const countedEvents = row.countEvents
            .filter((event) => this.isCountedEvent(event))
            .sort((a, b) => dayjs(b.updatedOn).valueOf() - dayjs(a.updatedOn).valueOf());

        return countedEvents.length > 0 ? (countedEvents[0].debtDelta ?? 0) : 0;
    }

    private async unmarkWorkerAsCounted(workerId: string): Promise<void> {
        const previousState = this.state;
        const { countPeriod, notCountedWorkerIds, rows } = previousState;

        if (!countPeriod || notCountedWorkerIds.includes(workerId)) {
            return;
        }

        const row = rows.find((r) => r.workerId === workerId);
        if (!row) {
            return;
        }

        const appliedDelta = this.getLastAppliedDebtDelta(row);
        const oldDebt = row.debt;
        const revertedDebt = this.roundMoney(oldDebt - appliedDelta);

        const now = dayjs();
        const optimisticUncountedEvent: ManualCountWorkerCountEvent = {
            id: "",
            countPeriodId: countPeriod.id,
            workerId,
            action: CountPeriodWorkerCountAction.Uncounted,
            updatedBy: this.context.userId,
            updatedByName: this.context.fullName || this.context.userId,
            updatedOn: now.toISOString(),
            debtDelta: -appliedDelta
        };

        const updatedCountPeriod: CountPeriod = {
            ...countPeriod,
            countedWorkers: countPeriod.countedWorkers.filter((id) => id !== workerId),
            countUpdatedBy: [this.context.userId],
            countUpdatedOn: now
        };

        const updatedNotCountedWorkerIds = [...previousState.notCountedWorkerIds, workerId];
        const updatedRows = previousState.rows.map((r) =>
            r.workerId === workerId
                ? {
                    ...r,
                    debt: revertedDebt,
                    isCounted: false,
                    countEvents: [...r.countEvents, optimisticUncountedEvent],
                    hasUncountedEvent: true
                }
                : r
        );

        this.setState({
            isLoading: true,
            countPeriod: updatedCountPeriod,
            notCountedWorkerIds: updatedNotCountedWorkerIds,
            rows: updatedRows,
            uncountedPopoverAnchor: null,
            selectedCountEvents: [],
            selectedCountEventsWorkerName: "",
            errorTitle: "",
            errorMsg: ""
        });

        // Remove the counted mark first, then revert the debt that counting
        // added. If the debt revert fails, re-add the mark to stay consistent.
        const removeResult = await CountPeriodRepository.removeCountedWorker(
            this.context.locationId,
            countPeriod.id,
            workerId,
            -appliedDelta
        );

        if (removeResult.isFailed() || !removeResult.result) {
            this.restoreCountState(previousState, removeResult.errorTitle, removeResult.errorMessage, "Could not uncheck worker as counted");
            return;
        }

        const debtResult = await WorkerRepository.setWorkerDebt(this.context.locationId, workerId, revertedDebt);
        if (debtResult.isFailed()) {
            await CountPeriodRepository.addCountedWorker(this.context.locationId, countPeriod.id, workerId, appliedDelta);
            this.restoreCountState(previousState, debtResult.errorTitle, debtResult.errorMessage, "Could not revert the worker's debt");
            return;
        }

        await this.loadMonth(this.state.selectedMonth);
    }

    private async toggleWorkerCounted(workerId: string, isCounted: boolean): Promise<void> {
        if (isCounted) {
            await this.markWorkerAsCounted(workerId);
            return;
        }

        await this.unmarkWorkerAsCounted(workerId);
    }

    private openUncountedPopover(event: React.MouseEvent<HTMLElement>, workerName: string, countEvents: ManualCountWorkerCountEvent[]): void {
        event.stopPropagation();

        this.setState({
            uncountedPopoverAnchor: event.currentTarget,
            selectedCountEvents: countEvents,
            selectedCountEventsWorkerName: workerName
        });
    }

    private closeUncountedPopover(): void {
        this.setState({
            uncountedPopoverAnchor: null,
            selectedCountEvents: [],
            selectedCountEventsWorkerName: ""
        });
    }

    private openTipEditPopover(event: React.MouseEvent<HTMLElement>, row: ManualCountWorkerRow): void {
        event.stopPropagation();

        this.setState({
            tipEditAnchor: event.currentTarget,
            tipEditWorkerId: row.workerId,
            tipEditValue: this.getPlannedPaidTip(row).toFixed(2)
        });
    }

    private closeTipEditPopover(): void {
        this.setState({
            tipEditAnchor: null,
            tipEditWorkerId: null,
            tipEditValue: ""
        });
    }

    private parseTipEditValue(): number | null {
        const raw = this.state.tipEditValue.trim().replace(",", ".");
        if (!raw) {
            return null;
        }

        const value = Number.parseFloat(raw);
        return Number.isFinite(value) ? this.roundMoney(value) : null;
    }

    private applyTipEdit(): void {
        const { tipEditWorkerId, rows } = this.state;
        const row = rows.find((r) => r.workerId === tipEditWorkerId);
        const value = this.parseTipEditValue();

        if (!tipEditWorkerId || !row || value === null) {
            return;
        }

        // Storing a value equal to the calculated payout would be a no-op
        // override; treat it as going back to automatic.
        const overrides = { ...this.state.manualTipOverrides };
        if (value === this.getCalculatedPaidTip(row)) {
            delete overrides[tipEditWorkerId];
        } else {
            overrides[tipEditWorkerId] = value;
        }

        this.setState({
            manualTipOverrides: overrides,
            tipEditAnchor: null,
            tipEditWorkerId: null,
            tipEditValue: ""
        });
    }

    private resetTipEdit(): void {
        const { tipEditWorkerId } = this.state;
        if (!tipEditWorkerId) {
            return;
        }

        const overrides = { ...this.state.manualTipOverrides };
        delete overrides[tipEditWorkerId];

        this.setState({
            manualTipOverrides: overrides,
            tipEditAnchor: null,
            tipEditWorkerId: null,
            tipEditValue: ""
        });
    }

    private getCountEventLabel(countEvent: ManualCountWorkerCountEvent): string {
        if (this.isCountedEvent(countEvent)) {
            return "Checked";
        }

        if (this.isUncountedEvent(countEvent)) {
            return "Unchecked";
        }

        return "Updated";
    }

    private getCountEventColor(countEvent: ManualCountWorkerCountEvent): string {
        if (this.isCountedEvent(countEvent)) {
            return "#166534";
        }

        if (this.isUncountedEvent(countEvent)) {
            return "#b45309";
        }

        return "rgba(0,0,0,0.55)";
    }

    private formatCurrency(n: number): string {
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: "EUR",
            maximumFractionDigits: 2
        }).format(n || 0);
    }

    private getIsCounted(): boolean {
        return this.state.notCountedWorkerIds.length === 0 && this.state.rows.length > 0;
    }

    private getCountedWorkersLength(): number {
        return this.state.countPeriod?.countedWorkers.length ?? 0;
    }

    private getCountedByName(): string | null {
        const countedBy = this.state.countPeriod?.countUpdatedBy;

        if (!countedBy || countedBy.length === 0) {
            return null;
        }

        const worker = this.state.workers.find((x) => x.UserId === countedBy[0]);
        if (!worker) {
            return countedBy[0];
        }

        return `${worker.FirstName} ${worker.LastName}`.trim();
    }

    private renderZoneCaption(label: string): React.ReactNode {
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 13, opacity: 0.9, whiteSpace: "nowrap" }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: this.accent, display: "inline-block" }} />
                {label}
            </div>
        );
    }

    private renderStatItem(label: string, value: string): React.ReactNode {
        return (
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3, minWidth: 0 }}>
                <span style={{ fontSize: 9.5, textTransform: "uppercase", letterSpacing: 0.5, color: "rgba(0,0,0,0.5)", fontWeight: 700, whiteSpace: "nowrap" }}>
                    {label}
                </span>
                <span style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {value}
                </span>
            </div>
        );
    }

    private renderMetaTile(icon: React.ReactNode, label: string, value: string): React.ReactNode {
        return (
            <div
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "5px 10px",
                    borderRadius: 10,
                    background: "rgba(0,0,0,0.025)",
                    border: `1px solid ${this.border}`,
                    minWidth: 0
                }}
            >
                {icon}
                <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15, minWidth: 0 }}>
                    <span style={{ fontSize: 9.5, textTransform: "uppercase", letterSpacing: 0.5, color: "rgba(0,0,0,0.5)", fontWeight: 700, whiteSpace: "nowrap" }}>
                        {label}
                    </span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {value}
                    </span>
                </div>
            </div>
        );
    }

    // One combined band under the page title: month status & counting
    // progress, the month's totals, and the received-amount entry. Replaces
    // the four KPI tiles plus the separate status and received cards.
    private renderOverviewCard(): React.ReactNode {
        const { rows, countPeriod, receivedPin, receivedCash, receivedTouched } = this.state;

        const isCounted = this.getIsCounted();
        const countedCount = rows.filter((row) => row.isCounted).length;
        const progress = rows.length > 0 ? (countedCount / rows.length) * 100 : 0;

        const statusBorder = isCounted ? "#86efac" : "#fdba74";
        const statusChipBg = isCounted ? "#dcfce7" : "#ffedd5";
        const statusText = isCounted ? "#166534" : "#9a3412";
        const statusLabel = isCounted ? "Counted" : this.getCountedWorkersLength() > 0 ? "In progress" : "Not started";

        const countedByName = this.getCountedByName();
        const countedOn = countPeriod?.countUpdatedOn && dayjs(countPeriod.countUpdatedOn).isValid()
            ? dayjs(countPeriod.countUpdatedOn)
            : null;

        const totalDebt = rows.reduce((sum, x) => sum + x.debt, 0);
        const totalPin = rows.reduce((sum, x) => sum + x.pinTip, 0);
        const totalCash = rows.reduce((sum, x) => sum + x.cashTip, 0);

        const receivedSaved = Boolean(countPeriod?.transactionId);
        const rawPin = receivedPin.trim();
        const parsedPin = Number.parseFloat(rawPin.replace(",", "."));
        const rawCash = receivedCash.trim();
        const parsedCash = Number.parseFloat(rawCash.replace(",", "."));
        const receivedInvalid = receivedTouched && (!rawPin || !rawCash || Number.isNaN(parsedPin) || Number.isNaN(parsedCash));
        const totalReceived = !Number.isNaN(parsedPin) && !Number.isNaN(parsedCash) ? parsedPin + parsedCash : null;

        const verticalRule = () => <div aria-hidden style={{ background: "rgba(0,0,0,0.06)" }} />;

        return (
            <div
                style={{
                    background: "#FFFFFF",
                    borderRadius: 14,
                    boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                    border: `1px solid ${this.border}`,
                    padding: "14px 16px",
                    boxSizing: "border-box",
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1.25fr) 1px minmax(0, 1fr) 1px minmax(0, 1.45fr)",
                    gap: 16,
                    alignItems: "stretch"
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                        {this.renderZoneCaption("Count status")}

                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "4px 10px",
                                borderRadius: 999,
                                background: statusChipBg,
                                border: `1px solid ${statusBorder}`,
                                color: statusText,
                                fontWeight: 700,
                                fontSize: 12,
                                lineHeight: 1,
                                whiteSpace: "nowrap"
                            }}
                        >
                            {isCounted ? (
                                <CheckCircleRoundedIcon sx={{ fontSize: 15 }} />
                            ) : (
                                <HourglassEmptyRoundedIcon sx={{ fontSize: 15 }} />
                            )}
                            {statusLabel}
                        </span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11.5, color: "rgba(0,0,0,0.55)", fontWeight: 600 }}>
                            <span>{countedCount} of {rows.length} workers counted</span>
                            <span style={{ fontVariantNumeric: "tabular-nums" }}>{Math.round(progress)}%</span>
                        </div>
                        <InlineProgressBar value={progress} height={7} accentColor={this.accent} ariaLabel="Counting progress" />
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: "auto" }}>
                        {this.renderMetaTile(
                            <PersonOutlineRoundedIcon sx={{ fontSize: 17, color: this.accent }} />,
                            "Updated by",
                            countedByName ?? "—"
                        )}
                        {this.renderMetaTile(
                            <ScheduleRoundedIcon sx={{ fontSize: 17, color: this.accent }} />,
                            "Updated on",
                            countedOn ? countedOn.format("DD MMM YYYY HH:mm") : "—"
                        )}
                    </div>
                </div>

                {verticalRule()}

                <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
                    {this.renderZoneCaption("Month totals")}

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px", flex: 1, alignContent: "center" }}>
                        {this.renderStatItem("Workers", `${rows.length}`)}
                        {this.renderStatItem("Total debt", this.formatCurrency(totalDebt))}
                        {this.renderStatItem("Pin tips", this.formatCurrency(totalPin))}
                        {this.renderStatItem("Cash tips", this.formatCurrency(totalCash))}
                    </div>
                </div>

                {verticalRule()}

                <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                        {this.renderZoneCaption("Received amount")}

                        {receivedSaved ? (
                            <Chip
                                icon={<CheckCircleRoundedIcon sx={{ fontSize: 15 }} />}
                                label="Saved"
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    borderRadius: 999,
                                    background: "#dcfce7",
                                    border: "1px solid #86efac",
                                    color: "#166534",
                                    height: 24,
                                    "& .MuiChip-icon": { color: "#166534", ml: 0.5 }
                                }}
                            />
                        ) : (
                            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, whiteSpace: "nowrap" }}>
                                {totalReceived !== null ? `Total ${this.formatCurrency(totalReceived)}` : "Enter pin amount"}
                            </Typography>
                        )}
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr auto",
                            gap: 10,
                            alignItems: "end",
                            marginTop: "auto"
                        }}
                    >
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1 }}>
                                Pin
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                value={receivedPin}
                                onChange={this.onReceivedPinChange}
                                error={receivedInvalid}
                                disabled={receivedSaved || this.state.isLoading}
                                placeholder="0.00"
                                slotProps={{
                                    htmlInput: { inputMode: "decimal" },
                                    input: {
                                        startAdornment: <InputAdornment position="start">€</InputAdornment>
                                    }
                                }}
                            />
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1 }}>
                                Cash <span style={{ fontWeight: 500, opacity: 0.7 }}>· auto</span>
                            </Typography>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    height: 40,
                                    padding: "0 12px",
                                    borderRadius: 8,
                                    background: "rgba(0,0,0,0.03)",
                                    border: `1px solid ${this.border}`,
                                    color: "rgba(0,0,0,0.7)",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    fontVariantNumeric: "tabular-nums",
                                    boxSizing: "border-box"
                                }}
                            >
                                <span style={{ opacity: 0.6 }}>€</span>
                                {receivedCash || "0.00"}
                            </div>
                        </div>

                        <Button
                            variant="contained"
                            onClick={this.onReceivedSubmit}
                            disabled={receivedSaved || this.state.isLoading}
                            sx={{
                                borderRadius: 2,
                                backgroundColor: this.accent,
                                "&:hover": { backgroundColor: "#b78445" },
                                textTransform: "none",
                                fontWeight: 700,
                                minWidth: 72,
                                height: 40,
                                boxShadow: "none"
                            }}
                        >
                            Add
                        </Button>
                    </div>

                    {receivedInvalid ? (
                        <Typography variant="caption" sx={{ color: "#d32f2f", lineHeight: 1 }}>
                            Fill in the pin amount with a valid number
                        </Typography>
                    ) : null}
                </div>
            </div>
        );
    }

    private PaperCard(opts: {
        title: string;
        minHeight?: number;
        children?: React.ReactNode;
        headerRight?: React.ReactNode;
    }) {
        const { title, minHeight = 100, children, headerRight } = opts;

        return (
            <div
                role="region"
                aria-label={title}
                style={{
                    background: "#FFFFFF",
                    borderRadius: 14,
                    boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                    padding: "12px 8px 8px 8px",
                    boxSizing: "border-box",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    minHeight,
                    height: "100%",
                    overflow: "hidden"
                }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 13, opacity: 0.9 }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: this.accent, display: "inline-block" }} />
                        {title}
                    </div>
                    {headerRight}
                </div>

                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        alignItems: "stretch",
                        justifyContent: "stretch",
                        overflow: "hidden"
                    }}
                >
                    {children ? (
                        <div
                            style={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                minHeight: 0,
                                overflow: "hidden"
                            }}
                        >
                            {children}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }

    public render() {
        const { rows, selectedMonth } = this.state;

        const selectedCountEvents = [...this.state.selectedCountEvents].sort((a, b) => dayjs(a.updatedOn).valueOf() - dayjs(b.updatedOn).valueOf());

        const tipEditRow = rows.find((r) => r.workerId === this.state.tipEditWorkerId) ?? null;
        const tipEditParsed = this.parseTipEditValue();
        const tipEditCalculated = tipEditRow ? this.getCalculatedPaidTip(tipEditRow) : 0;
        const tipEditNewDebt = tipEditRow && tipEditParsed !== null
            ? this.roundMoney(tipEditRow.debt + tipEditRow.totalTip - tipEditParsed)
            : null;
        const tipEditHasOverride = tipEditRow ? this.state.manualTipOverrides[tipEditRow.workerId] !== undefined : false;

        const totalDebt = rows.reduce((sum, x) => sum + x.debt, 0);
        const totalPin = rows.reduce((sum, x) => sum + x.pinTip, 0);
        const totalCash = rows.reduce((sum, x) => sum + x.cashTip, 0);
        const totalTips = rows.reduce((sum, x) => sum + this.getDisplayedTotalTip(x), 0);

        return (
            <div style={{ width: "100%", height: "100%", position: "relative" }}>
                <ErrorDialogue
                    show={Boolean(this.state.errorMsg)}
                    title={this.state.errorTitle ?? ""}
                    message={this.state.errorMsg ?? ""}
                    onClose={() => this.setState({ errorTitle: undefined, errorMsg: undefined })}
                />

                <Popover
                    open={Boolean(this.state.payoutOptionsAnchor)}
                    anchorEl={this.state.payoutOptionsAnchor}
                    onClose={() => this.setState({ payoutOptionsAnchor: null })}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: 2,
                                border: "1px solid rgba(0,0,0,0.08)",
                                boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
                                p: 2,
                                width: 340,
                                maxWidth: "calc(100vw - 32px)"
                            }
                        }
                    }}
                >
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                        Payout options
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#1a1a1a", mb: 1.5 }}>
                        Denominations & rounding
                    </Typography>
                    <CurrencyRoundingSetting compact accent={this.accent} onChange={this.onCurrencySettingsChange} />
                </Popover>

                <Popover
                    open={Boolean(this.state.uncountedPopoverAnchor)}
                    anchorEl={this.state.uncountedPopoverAnchor}
                    onClose={this.closeUncountedPopover}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: 2,
                                border: "1px solid rgba(0,0,0,0.08)",
                                boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
                                p: 1.5,
                                width: 320,
                                maxWidth: "calc(100vw - 32px)"
                            }
                        }
                    }}
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, lineHeight: 1 }}>
                            Count history
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: "#1a1a1a", lineHeight: 1.2, mt: -0.5 }}>
                            {this.state.selectedCountEventsWorkerName || "Worker"}
                        </Typography>

                        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 280, overflow: "auto" }}>
                            {selectedCountEvents.length === 0 ? (
                                <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.2 }}>
                                    No count actions found.
                                </Typography>
                            ) : (
                                selectedCountEvents.map((countEvent, index) => {
                                    const countEventOn = countEvent.updatedOn && dayjs(countEvent.updatedOn).isValid()
                                        ? dayjs(countEvent.updatedOn)
                                        : null;
                                    const color = this.getCountEventColor(countEvent);

                                    return (
                                        <div
                                            key={countEvent.id || `${countEvent.workerId}-${countEvent.action}-${countEvent.updatedOn}-${index}`}
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "24px 1fr",
                                                gap: 8,
                                                alignItems: "start"
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 22,
                                                    height: 22,
                                                    borderRadius: 8,
                                                    display: "grid",
                                                    placeItems: "center",
                                                    background: `${color}14`,
                                                    color
                                                }}
                                            >
                                                {this.isCountedEvent(countEvent) ? (
                                                    <CheckCircleRoundedIcon sx={{ fontSize: 15 }} />
                                                ) : (
                                                    <ErrorOutlineRoundedIcon sx={{ fontSize: 15 }} />
                                                )}
                                            </div>

                                            <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.15, color }}>
                                                    {this.getCountEventLabel(countEvent)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: "#1a1a1a", lineHeight: 1.2, fontWeight: 600 }}>
                                                    {countEvent.updatedByName || countEvent.updatedBy || "Unknown"}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.2 }}>
                                                    {countEventOn ? countEventOn.format("DD MMM YYYY HH:mm") : "Date unknown"}
                                                </Typography>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </Popover>

                <Popover
                    open={Boolean(this.state.tipEditAnchor)}
                    anchorEl={this.state.tipEditAnchor}
                    onClose={this.closeTipEditPopover}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    transformOrigin={{ vertical: "top", horizontal: "center" }}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: 2,
                                border: "1px solid rgba(0,0,0,0.08)",
                                boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
                                p: 1.5,
                                width: 280,
                                maxWidth: "calc(100vw - 32px)"
                            }
                        }
                    }}
                >
                    {tipEditRow ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, lineHeight: 1 }}>
                                    Paid tip
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 800, color: "#1a1a1a", lineHeight: 1.2 }}>
                                    {tipEditRow.name}
                                </Typography>
                            </div>

                            <TextField
                                autoFocus
                                size="small"
                                fullWidth
                                value={this.state.tipEditValue}
                                onChange={(e) => this.setState({ tipEditValue: e.target.value })}
                                onKeyDown={(e) => { if (e.key === "Enter") this.applyTipEdit(); }}
                                error={tipEditParsed === null}
                                placeholder="0.00"
                                slotProps={{
                                    htmlInput: { inputMode: "decimal" },
                                    input: {
                                        startAdornment: <InputAdornment position="start">€</InputAdornment>
                                    }
                                }}
                            />

                            <div style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5, lineHeight: 1.3 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, color: "rgba(0,0,0,0.55)" }}>
                                    <span>Calculated payout</span>
                                    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                                        {this.formatCurrency(tipEditCalculated)}
                                    </span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, color: "rgba(0,0,0,0.55)" }}>
                                    <span>Debt after counting</span>
                                    <span
                                        style={{
                                            fontVariantNumeric: "tabular-nums",
                                            fontWeight: 700,
                                            color: tipEditNewDebt === null
                                                ? "#d32f2f"
                                                : tipEditNewDebt > 0
                                                    ? "#12a037ff"
                                                    : tipEditNewDebt < 0
                                                        ? "#c52626ff"
                                                        : "rgba(0,0,0,0.6)"
                                        }}
                                    >
                                        {tipEditNewDebt === null ? "Enter a valid amount" : this.formatCurrency(tipEditNewDebt)}
                                    </span>
                                </div>
                            </div>

                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                {tipEditHasOverride ? (
                                    <Button
                                        size="small"
                                        onClick={this.resetTipEdit}
                                        sx={{ textTransform: "none", fontWeight: 700, color: "rgba(0,0,0,0.55)", px: 1 }}
                                    >
                                        Reset to calculated
                                    </Button>
                                ) : (
                                    <span />
                                )}

                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={this.applyTipEdit}
                                    disabled={tipEditParsed === null}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 700,
                                        borderRadius: 999,
                                        px: 2,
                                        backgroundColor: this.accent,
                                        "&:hover": { backgroundColor: "#b78445" },
                                        boxShadow: "none"
                                    }}
                                >
                                    Apply
                                </Button>
                            </div>
                        </div>
                    ) : null}
                </Popover>

                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        boxSizing: "border-box",
                        padding: 16,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        gap: 12
                    }}
                >
                    {this.state.isLoading && <Spinner />}

                    <div
                        style={{
                            width: "100%",
                            maxWidth: 1600,
                            margin: "0 auto",
                            boxSizing: "border-box",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            gap: 12,
                            flexWrap: "wrap",
                            flexShrink: 0
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                            <div style={{ width: 6, height: 34, background: this.accent, borderRadius: 6 }} />
                            <div
                                style={{
                                    fontSize: 26,
                                    fontWeight: 800,
                                    lineHeight: 1.1,
                                    color: "#1a1a1a",
                                    letterSpacing: "0.2px",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                Manual count
                            </div>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 10,
                                flexWrap: "wrap",
                                marginLeft: "auto"
                            }}
                        >
                            <Button
                                variant="outlined"
                                startIcon={<TuneRoundedIcon />}
                                onClick={(e) => this.setState({ payoutOptionsAnchor: e.currentTarget })}
                                disabled={this.state.isLoading}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    borderRadius: 999,
                                    px: 2,
                                    py: 0.8,
                                    background: "#fff",
                                    borderColor: `${this.accent}55`,
                                    color: "#1a1a1a",
                                    boxShadow: "0 4px 14px rgba(0,0,0,0.04)"
                                }}
                            >
                                Payout options
                            </Button>

                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "4px 8px",
                                    borderRadius: 999,
                                    background: "#fff",
                                    border: `1px solid ${this.border}`,
                                    boxShadow: "0 4px 14px rgba(0,0,0,0.04)"
                                }}
                            >
                                <Tooltip title="Previous month">
                                    <IconButton size="small" onClick={this.goToPreviousMonth} disabled={this.state.isLoading}>
                                        <ChevronLeftRoundedIcon />
                                    </IconButton>
                                </Tooltip>

                                <Typography variant="body1" sx={{ minWidth: 160, textAlign: "center", fontWeight: 700 }}>
                                    {selectedMonth.format("MMMM YYYY")}
                                </Typography>

                                <Tooltip title="Next month">
                                    <IconButton
                                        size="small"
                                        onClick={this.goToNextMonth}
                                        disabled={
                                            this.state.isLoading ||
                                            selectedMonth.isSame(dayjs().startOf("month"), "month")
                                        }
                                    >
                                        <ChevronRightRoundedIcon />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            width: "100%",
                            maxWidth: 1600,
                            margin: "0 auto",
                            boxSizing: "border-box",
                            flexShrink: 0
                        }}
                    >
                        {this.renderOverviewCard()}
                    </div>

                    <div
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflow: "hidden",
                            width: "100%",
                            maxWidth: 1600,
                            margin: "0 auto"
                        }}
                    >
                        {this.PaperCard({
                            title: "Worker overview",
                            minHeight: 0,
                            headerRight: (
                                <Chip
                                    label={`Total tips: ${this.formatCurrency(totalTips)}`}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        background: `${this.accent}14`,
                                        border: `1px solid ${this.accent}33`,
                                        height: 24
                                    }}
                                />
                            ),
                            children: (
                                <div
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        minHeight: 0,
                                        height: "100%"
                                    }}
                                >
                                    <div
                                        style={{
                                            position: "sticky",
                                            top: 0,
                                            zIndex: 2,
                                            backdropFilter: "saturate(180%) blur(8px)",
                                            background: "linear-gradient(180deg, rgba(255,255,255,0.94), rgba(255,255,255,0.82))",
                                            borderBottom: "1px solid rgba(0,0,0,0.08)",
                                            display: "grid",
                                            gridTemplateColumns: "1.1fr 1.5fr 1.5fr 0.8fr 0.8fr 1.5fr 78px",
                                            alignItems: "center",
                                            padding: "6px 10px",
                                            minHeight: 30,
                                            boxSizing: "border-box",
                                            borderRadius: 10,
                                            columnGap: 6,
                                            flexShrink: 0
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary">Function</Typography>
                                        <Typography variant="caption" color="text.secondary">Name</Typography>
                                        <Typography variant="caption" color="text.secondary">Debt</Typography>
                                        <Typography variant="caption" color="text.secondary">Pin tip</Typography>
                                        <Typography variant="caption" color="text.secondary">Cash tip</Typography>
                                        <Typography variant="caption" color="text.secondary">Total tip</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                                            Counted
                                        </Typography>
                                    </div>

                                    <div style={{ flex: 1, minHeight: 0, overflow: "auto", marginTop: 4 }}>
                                        {rows.length === 0 ? (
                                            <div
                                                style={{
                                                    minHeight: 180,
                                                    display: "grid",
                                                    placeItems: "center",
                                                    color: "rgba(0,0,0,0.5)",
                                                    fontWeight: 600
                                                }}
                                            >
                                                No data for {selectedMonth.format("MMMM YYYY")}
                                            </div>
                                        ) : (
                                            <>
                                                {rows.map((row, index) => (
                                                    <div
                                                        key={row.workerId}
                                                        style={{
                                                            display: "grid",
                                                            gridTemplateColumns: "1.1fr 1.5fr 1.5fr 0.8fr 0.8fr 1.5fr 78px",
                                                            alignItems: "center",
                                                            padding: "4px 10px",
                                                            borderBottom: "1px solid rgba(0,0,0,0.05)",
                                                            backgroundColor: index % 2 === 0 ? "#ffffff" : "rgba(0,0,0,0.012)",
                                                            borderRadius: 6,
                                                            columnGap: 6,
                                                            minHeight: 30
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12.5, lineHeight: 1.1 }}>
                                                            {Functions[row.function] || "—"}
                                                        </Typography>

                                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12.5, lineHeight: 1.1 }}>
                                                            {row.name}
                                                        </Typography>

                                                        {this.renderDebtPreviewCell(row)}

                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                fontSize: 12.5,
                                                                lineHeight: 1.1,
                                                                color: "#1a1a1a",
                                                                fontVariantNumeric: "tabular-nums"
                                                            }}
                                                        >
                                                            {this.formatCurrency(row.pinTip)}
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                fontSize: 12.5,
                                                                lineHeight: 1.1,
                                                                color: "#1a1a1a",
                                                                fontVariantNumeric: "tabular-nums"
                                                            }}
                                                        >
                                                            {this.formatCurrency(row.cashTip)}
                                                        </Typography>

                                                        {this.renderTotalTipCell(row)}

                                                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
                                                            <Checkbox
                                                                size="small"
                                                                checked={row.isCounted}
                                                                disabled={this.state.isLoading}
                                                                onChange={(event) => this.toggleWorkerCounted(row.workerId, event.target.checked)}
                                                                sx={{
                                                                    padding: "2px",
                                                                    color: `${this.accent}aa`,
                                                                    "&.Mui-checked": {
                                                                        color: this.accent
                                                                    }
                                                                }}
                                                            />
                                                            {row.hasUncountedEvent ? (
                                                                <Tooltip title="Show count history">
                                                                    <IconButton
                                                                        size="small"
                                                                        aria-label={`Show count history for ${row.name}`}
                                                                        onClick={(event) => this.openUncountedPopover(event, row.name, row.countEvents)}
                                                                        sx={{
                                                                            width: 24,
                                                                            height: 24,
                                                                            color: "#b45309",
                                                                            padding: 0.25
                                                                        }}
                                                                    >
                                                                        <ErrorOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            ) : (
                                                                <span style={{ width: 24, height: 24, display: "inline-block" }} />
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}

                                                <div
                                                    style={{
                                                        display: "grid",
                                                        gridTemplateColumns: "1.1fr 1.5fr 1.5fr 0.8fr 0.8fr 1.5fr 78px",
                                                        alignItems: "center",
                                                        padding: "6px 10px",
                                                        marginTop: 3,
                                                        borderTop: `2px solid ${this.accent}33`,
                                                        background: `${this.accent}0d`,
                                                        borderRadius: 8,
                                                        columnGap: 6,
                                                        minHeight: 32
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 12.5, lineHeight: 1.1 }}>
                                                        Total
                                                    </Typography>

                                                    <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 12.5, lineHeight: 1.1 }}>
                                                        {rows.length} workers
                                                    </Typography>

                                                    <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums", fontSize: 12.5, lineHeight: 1.1 }}>
                                                        {this.formatCurrency(totalDebt)}
                                                    </Typography>

                                                    <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums", fontSize: 12.5, lineHeight: 1.1 }}>
                                                        {this.formatCurrency(totalPin)}
                                                    </Typography>

                                                    <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums", fontSize: 12.5, lineHeight: 1.1 }}>
                                                        {this.formatCurrency(totalCash)}
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 900,
                                                            fontVariantNumeric: "tabular-nums",
                                                            color: "#12a037ff",
                                                            fontSize: 12.5,
                                                            lineHeight: 1.1
                                                        }}
                                                    >
                                                        {this.formatCurrency(totalTips)}
                                                    </Typography>

                                                    <div />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        );
    }
}
